/** Side gradients (769px+): opacity from measured card peek at row edges, not gap. */
export const createTestimonialsEdgeFade = ({
    container,
    slides,
    isRtl,
    getActiveIndex,
    getMaxSlideIndex,
    isScrollable,
    isEdgeFadeEnabled,
}) => {
    let raf = null;
    const enabled = () => isScrollable() && isEdgeFadeEnabled();
    const rowRect = () => container.querySelector(':scope > .row')?.getBoundingClientRect();

    const setFade = (right, left) => {
        const clamp = (n) => Math.max(0, Math.min(1, n));
        container.style.setProperty('--testimonials-edge-fade-right', String(clamp(right)));
        container.style.setProperty('--testimonials-edge-fade-left', String(clamp(left)));
    };

    const fadeOpacity = (px) => (px <= 40 ? 0 : px >= 120 ? 1 : (px - 40) / 80);
    const easeInOut = (t) => (t < 0.5 ? 4 * t ** 3 : 1 - (-2 * t + 2) ** 3 / 2);

    const measurePeeks = (rect) => {
        let trailingPx = 0;
        let leadingPx = 0;
        slides.forEach((slide) => {
            const box = (slide.querySelector('.testimonial-card') ?? slide).getBoundingClientRect();
            if (box.right <= rect.left || box.left >= rect.right) return;
            if (isRtl) {
                trailingPx = Math.max(trailingPx, rect.left - box.left);
                leadingPx = Math.max(leadingPx, box.right - rect.right);
            } else {
                trailingPx = Math.max(trailingPx, box.right - rect.right);
                leadingPx = Math.max(leadingPx, rect.left - box.left);
            }
        });
        return [Math.max(0, trailingPx), Math.max(0, leadingPx)];
    };

    const opacitiesFor = (index, maxIndex, trailingPx, leadingPx) => [
        index >= maxIndex || trailingPx <= 0 ? 0 : fadeOpacity(trailingPx),
        index <= 0 || leadingPx <= 0 ? 0 : fadeOpacity(leadingPx),
    ];

    const paint = (index) => {
        if (!enabled()) return setFade(0, 0);
        const rect = rowRect();
        if (!rect) return;
        setFade(...opacitiesFor(index, getMaxSlideIndex(), ...measurePeeks(rect)));
    };

    const slideMs = () => {
        const raw = slides[0] && getComputedStyle(slides[0]).transitionDuration.split(',')[0]?.trim();
        if (!raw) return 300;
        return raw.endsWith('ms') ? parseFloat(raw) || 300 : (parseFloat(raw) || 0.3) * 1000;
    };

    return {
        sync: () => paint(getActiveIndex()),

        animateWithSlide(fromIndex, toIndex) {
            if (raf) cancelAnimationFrame(raf);
            const duration = slideMs();
            const started = performance.now();

            const tick = (now) => {
                if (!enabled()) return setFade(0, 0);
                const rect = rowRect();
                if (!rect) return;

                const maxIndex = getMaxSlideIndex();
                const peeks = measurePeeks(rect);
                const t = easeInOut(Math.min(1, (now - started) / duration));
                const from = opacitiesFor(fromIndex, maxIndex, ...peeks);
                const to = opacitiesFor(toIndex, maxIndex, ...peeks);

                setFade(from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t);

                if (now - started < duration) {
                    raf = requestAnimationFrame(tick);
                    return;
                }

                raf = null;
                paint(toIndex);
            };

            raf = requestAnimationFrame(tick);
        },

        hideImmediateAtBounds(targetIndex) {
            if (!enabled()) return setFade(0, 0);
            const maxIndex = getMaxSlideIndex();
            let right = parseFloat(container.style.getPropertyValue('--testimonials-edge-fade-right')) || 0;
            let left = parseFloat(container.style.getPropertyValue('--testimonials-edge-fade-left')) || 0;
            if (targetIndex >= maxIndex) right = 0;
            if (targetIndex <= 0) left = 0;
            setFade(right, left);
        },
    };
};
